import { useCallback } from "react";
import {
  type AsyncOptions,
  type DeferFn,
  type PromiseFn,
  useAsync,
} from "react-async";
import { getFetchOptions } from "../../module/fetch.tsx";
import { URLS } from "../../routes.tsx";

export const doFetch = async (
  url: string,
  options = {},
): Promise<undefined | any> => {
  if (!url) {
    return null;
  }
  const res = await fetch(
    url === decodeURIComponent(url) ? encodeURI(url) : url,
    options,
  );

  if (res.status === 401) {
    window.location.reload();
  }

  // On ne redirige vers l'accueil que pour les requêtes GET,
  // un accès direct à une page/ressource interdite.
  // Pour les actions (DELETE/POST/PUT), on gère déjà l'erreur là où on l'appel
  // avec un toast et une redirection empêcherait de lire ce message.
  const method = (options as { method?: string })?.method ?? "GET";
  if (res.status === 403 && method.toUpperCase() === "GET") {
    document.location.href = URLS.ACCUEIL;
  }

  if (!res.ok) {
    throw res;
  }
  if (res.status === 204) {
    return null;
  }
  return res.text().then((text) => {
    return text ? JSON.parse(text) : null;
  });
};

export function useGet(url: string, asyncOptions: AsyncOptions<any> = {}) {
  return useAsync({
    ...asyncOptions,
    promiseFn: useCallback<PromiseFn<any>>(
      (props, { signal }) => {
        const options = getFetchOptions({
          signal,
          method: "GET",
        });
        return doFetch(url, options);
      },
      [url],
    ),
  });
}

/**
 * Permet de faire un appel à une URL en GET, au lancement via .run()
 */
export function useGetRun(url: string, asyncOptions: AsyncOptions<any> = {}) {
  return useAsync({
    ...asyncOptions,
    deferFn: useCallback<DeferFn<any>>(
      (args, props, { signal }) => {
        const options = getFetchOptions({
          signal,
          method: "GET",
        });
        return doFetch(url, options);
      },
      [url],
    ),
  });
}

export function usePost(
  url: string,
  asyncOptions: AsyncOptions<any> = {},
  isMultipartFormData = false,
) {
  return useAsync({
    ...asyncOptions,
    deferFn: useCallback<DeferFn<any>>(
      async ([body, { setSubmitting } = {}], props, { signal }) => {
        const options = isMultipartFormData
          ? getFetchOptions({
              signal,
              method: "POST",
              body,
            })
          : getFetchOptions({
              signal,
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(body),
            });

        try {
          return await doFetch(url, options);
        } finally {
          if (typeof setSubmitting === "function") {
            setSubmitting(false);
          }
        }
      },
      [isMultipartFormData, url],
    ),
  });
}

export function useDelete(url: string, asyncOptions: AsyncOptions<any> = {}) {
  return useAsync({
    ...asyncOptions,
    deferFn: useCallback<DeferFn<any>>(
      (args, props, { signal }) => {
        const options = getFetchOptions({
          signal,
          method: "DELETE",
        });
        return doFetch(url, options);
      },
      [url],
    ),
  });
}

export function usePut(
  url: string,
  asyncOptions: AsyncOptions<any> = {},
  isMultipartFormData: boolean,
) {
  return useAsync({
    ...asyncOptions,
    deferFn: useCallback<DeferFn<any>>(
      async ([body, { setSubmitting } = {}], props, { signal }) => {
        const options = isMultipartFormData
          ? getFetchOptions({
              signal,
              method: "PUT",
              body,
            })
          : getFetchOptions({
              signal,
              method: "PUT",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(body),
            });
        try {
          return await doFetch(url, options);
        } finally {
          if (typeof setSubmitting === "function") {
            setSubmitting(false);
          }
        }
      },
      [isMultipartFormData, url],
    ),
  });
}
